import React, { useCallback, useEffect, useMemo } from 'react';
import CheckoutLayout from '../../CheckoutLayout';
import { Input, Text } from '~/components';
import { useForm } from 'react-hook-form';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCheckout, useSalesFunnel } from '~/stores';
import useAppNavigation from '~/hooks/useAppNavigation';
import { Keyboard } from 'react-native';
import { isEmail } from '~/utils/yup';
import { useUserInfoStore } from '~/stores/userInfo';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '~/navigation/types';
import useMarisaAutoForm from '~/stores/marisaAutoForm';
import { getRawCellphone } from '~/utils/string';
import { Gender, MaritalStatus } from '~/utils/form';
import { CheckoutStepNames } from '~/utils/salesFunnel';

const formattedGender = {
  [Gender.MALE]: 'M',
  [Gender.FEMALE]: 'F',
  [Gender.NOT_INFORMED]: 'N',
};

const formattedMaritalStatus = {
  [MaritalStatus.MARRIED]: '2',
  [MaritalStatus.SINGLE]: '1',
  [MaritalStatus.WIDOWER]: '3',
  [MaritalStatus.DIVORCED]: '4',
};

interface InspectionSchema {
  email: string;
}

const schema = yup
  .object()
  .shape({
    email: isEmail(),
  })
  .required();

const Inspection: React.FC = () => {
  const { push } = useAppNavigation();
  const { user } = useUserInfoStore();
  const {
    setPage,
    insuranceFormData,
    setInsuranceFormData,
    form: checkoutForm,
  } = useCheckout();
  const { form: insuranceForm } = useMarisaAutoForm();
  const { newStepLog, setInfos } = useSalesFunnel();

  const { params } =
    useRoute<RouteProp<RootStackParamList, 'MarisaServicos_Checkout'>>();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<InspectionSchema>({
    resolver: yupResolver(schema),
  });

  const onSubmit = useCallback(
    async (data: InspectionSchema) => {
      Keyboard.dismiss();

      const formattedCellphone = getRawCellphone(
        insuranceForm?.phoneNumber ?? ''
      );

      const paymentInfo: CheckoutMarisaAuto.Payment = {
        code: checkoutForm!.paymentMethod?.code ?? '',
        installments: String(checkoutForm!.installment?.id),
        classType: checkoutForm!.paymentMethod?.classType ?? 1,
        card:
          checkoutForm?.paymentMethod?.method === 'credit-card' &&
          checkoutForm.creditCard
            ? checkoutForm.creditCard
            : undefined,
        debit:
          checkoutForm?.paymentMethod?.method === 'account-debit' &&
          checkoutForm.accountDebit
            ? checkoutForm.accountDebit
            : undefined,
      };

      const personalInfo: CheckoutMarisaAuto.PersonalInfo = {
        phone: {
          ddd: formattedCellphone.substring(0, 2),
          number: formattedCellphone.substring(2),
        },
        document: insuranceForm?.document ?? '',
        email: data.email,
        birthDate: insuranceForm?.personalInformation.birthDate ?? '',
        gender:
          formattedGender[insuranceForm?.personalInformation.gender as Gender],
        maritalStatus:
          formattedMaritalStatus[
            insuranceForm?.personalInformation.maritalStatus as MaritalStatus
          ],
        license: {
          number: insuranceFormData.registerNumber,
          date: insuranceFormData.dateOfIssue,
        },
      };

      setInsuranceFormData({
        address: insuranceForm?.address,
        personalInfo,
        paymentInfo,
      });

      setInfos({ email: data.email });

      const nextStep = (params.step || 0) + 1;
      setPage(nextStep);

      push('MarisaServicos_Checkout', { step: nextStep });
    },
    [
      checkoutForm,
      insuranceForm?.address,
      insuranceForm?.document,
      insuranceForm?.personalInformation.birthDate,
      insuranceForm?.personalInformation.gender,
      insuranceForm?.personalInformation.maritalStatus,
      insuranceForm?.phoneNumber,
      insuranceFormData.dateOfIssue,
      insuranceFormData.registerNumber,
      params.step,
      push,
      setInfos,
      setInsuranceFormData,
      setPage,
    ]
  );

  useEffect(() => {
    newStepLog(CheckoutStepNames.EMAIL);
  }, [newStepLog]);

  const firstName = useMemo(() => user?.name.split(' ')[0], [user?.name]);

  return (
    <CheckoutLayout isValid onSubmit={handleSubmit(onSubmit)}>
      <Text className="text-xl text-gray-600 mb-4">
        Informe o seu email para vistoria
      </Text>

      <Text className="text-sm text-gray-400 mb-8">
        {firstName && <Text className="text-primary-600">{firstName}, </Text>}
        agora nos informe seu email para enviarmos as informações a respeito da
        vistoria, apólice e outros documentos, além de sempre te manter
        informada.
      </Text>

      <Input
        control={control}
        name="email"
        label="Email"
        placeholder="Insira o seu email"
        error={errors.email?.message}
        maxLength={100}
        keyboardType="email-address"
        autoCapitalize="none"
      />
    </CheckoutLayout>
  );
};

export default Inspection;
